import axios from "axios";
import { getFileName } from "../utils/common";
import {
  multipartUpload,
  records,
  signinPartUpload,
  transcoder,
  completeMultipartUpload as completeMultipartUploadRoute,
  abortUpload as abortUploadRoute,
  videoShareLink,
} from "../constants/apiRoutes";
import { crudUtil } from "./axiosCRUD";
import logger from "../utils/logger";
import { IChunkUploadData } from "../types/videoUploader";

/**
 * NEW S3 DIRECT UPLOAD FUNCTIONS (local backend)
 * These replace the external Dadan API calls
 */

export async function createS3MultipartUpload({
  videoGuid,
  fileName,
  projectId,
  sceneId,
  isTemp,
  abortController,
}: {
  videoGuid: string;
  fileName: string;
  projectId?: number;
  sceneId?: number;
  isTemp?: boolean;
  abortController?: AbortController;
}): Promise<{ uploadId: string; key: string; isTemp?: boolean }> {
  const response = await crudUtil.post(
    '/api/upload/create-multipart',
    {
      videoGuid,
      fileName,
      projectId,
      sceneId,
      isTemp,
    },
    {
      signal: abortController?.signal,
    }
  );

  return { uploadId: response.uploadId, key: response.key, isTemp: response.isTemp };
}

export async function getS3SignedPartUrl({
  key,
  uploadId,
  partNumber,
  abortController,
}: {
  key: string;
  uploadId: string;
  partNumber: number;
  abortController?: AbortController;
}): Promise<string> {
  const response = await crudUtil.post(
    '/api/upload/sign-part',
    {
      key,
      uploadId,
      partNumber,
    },
    {
      signal: abortController?.signal,
    }
  );

  return response.url;
}

export async function completeS3MultipartUpload({
  key,
  uploadId,
  parts,
  micEnabled,
  videoGuid,
  duration,
  abortController,
}: {
  key: string;
  uploadId: string;
  parts: Array<{ PartNumber: number; ETag: string }>;
  micEnabled?: boolean;
  videoGuid?: string;
  duration?: number;
  abortController?: AbortController;
}): Promise<{ fileUrl: string; thumbnailUrl?: string }> {
  const response = await crudUtil.post(
    '/api/upload/complete-multipart',
    {
      key,
      uploadId,
      parts,
      micEnabled, // Pass micEnabled to backend
      videoGuid,  // Pass videoGuid for persistence across refresh
      duration,   // Pass duration for persistence across refresh
    },
    {
      signal: abortController?.signal,
    }
  );

  return {
    fileUrl: response.fileUrl,
    thumbnailUrl: response.thumbnailUrl || undefined
  };
}

export async function abortS3MultipartUpload({
  key,
  uploadId,
  abortController,
}: {
  key: string;
  uploadId: string;
  abortController?: AbortController;
}): Promise<void> {
  await crudUtil.post(
    '/api/upload/abort-multipart',
    {
      key,
      uploadId,
    },
    {
      signal: abortController?.signal,
    }
  );
}

/**
 * DADAN API FUNCTIONS (used for blank mode uploads)
 * These route through the local proxy to bypass CORS restrictions
 * Proxy routes: /api/proxy/transcoder/* -> https://app.dadan.io/api/Transcoder/*
 */

// Get records API - creates a video record in Dadan
export async function createDadanRecords({
  userId,
  fileName,
  authToken,
  abortController,
}: {
  userId: string;
  fileName: string;
  authToken: string;
  abortController?: AbortController;
}): Promise<string> {
  const objectToSend = {
    client_type: "upload",
    client_version: "1.0.0",
    browser_type: "chrome",
    browser_version: "",
    os: "",
    os_build_version: "",
    user_id: userId,
    resolution: 0,
    window_title: "",
    connection_speed: "",
    hardware_acceleration: "",
    file_name: getFileName(fileName),
  };

  const response = await crudUtil.post(
    '/api/proxy/transcoder/records',
    {
      segmentFormat: "ts",
      getVideoUploadLink: true,
      getManifestUploadLink: false,
      deviceSignature: JSON.stringify(objectToSend),
    },
    {
      signal: abortController?.signal,
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    }
  );

  const { videoId } = response.record;
  return videoId;
}

// Create multipart upload on Dadan
export async function createDadanMultipartUpload({
  videoId,
  authToken,
  abortController,
}: {
  videoId: string;
  authToken: string;
  abortController?: AbortController;
}): Promise<{ key: string; uploadId: string }> {
  const response = await crudUtil.post(
    '/api/proxy/transcoder/create-multipart-upload',
    {
      filename: videoId,
      type: "video/mp4",
    },
    {
      signal: abortController?.signal,
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    }
  );
  return { key: response.key, uploadId: response.uploadId };
}

// Get signed URL for part upload on Dadan
export async function getDadanSignedUrl({
  uploadId,
  key,
  partNumber,
  authToken,
  abortController,
}: {
  uploadId: string;
  key: string;
  partNumber: number;
  authToken: string;
  abortController?: AbortController;
}): Promise<{ url: string; headers?: any }> {
  logger.info(`Getting Dadan signed URL for part ${partNumber}`);

  const response = await crudUtil.post(
    '/api/proxy/transcoder/sign-part-upload',
    {
      key,
      uploadId,
      partNumber,
    },
    {
      signal: abortController?.signal,
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    }
  );
  logger.info(`Dadan signed URL for part ${partNumber} obtained successfully`);
  return {
    url: response.url,
    headers: response.headers,
  };
}

// Complete multipart upload on Dadan
export async function completeDadanMultipartUpload({
  uploadId,
  key,
  parts,
  authToken,
  abortController,
}: {
  uploadId: string;
  key: string;
  parts: Array<{ ETag: string; PartNumber: number; "content-length": string }>;
  authToken: string;
  abortController?: AbortController;
}): Promise<any> {
  const response = await crudUtil.post(
    '/api/proxy/transcoder/complete-multipart-upload',
    {
      uploadId,
      key,
      parts,
    },
    {
      signal: abortController?.signal,
      headers: {
        'Authorization': `Bearer ${authToken}`,
      },
    }
  );
  return response;
}

/**
 * LEGACY RELATIVE PATH FUNCTIONS (kept for backwards compatibility)
 */

// Get records API
export async function createRecords({
  userId,
  fileName,
  abortController,
}: {
  userId: string;
  fileName: string;
  abortController?: AbortController;
}) {
  const objectToSend = {
    client_type: "upload",
    client_version: "1.0.0",
    browser_type: "chrome",
    browser_version: "",
    os: "",
    os_build_version: "",
    user_id: userId,
    resolution: 0,
    window_title: "",
    connection_speed: "",
    hardware_acceleration: "",
    file_name: getFileName(fileName),
  };

  const response = await crudUtil.post(
    `${transcoder}${records}`,
    {
      segmentFormat: "ts",
      getVideoUploadLink: true,
      getManifestUploadLink: false,
      deviceSignature: JSON.stringify(objectToSend),
    },
    {
      signal: abortController?.signal,
    }
  );

  const { videoId } = response.record;
  return videoId;
}

export async function createMultipartUpload({
  videoId,
  abortController,
}: {
  videoId: string;
  abortController?: AbortController;
}) {
  const response = await crudUtil.post(
    `${transcoder}${multipartUpload}`,
    {
      filename: videoId,
      type: "video/mp4", // Assuming webm format
    },
    {
      signal: abortController?.signal,
    }
  );
  return { key: response.key, uploadId: response.uploadId };
}

// Get a signed URL for a specific chunk
export async function getSignedUrl({
  uploadId,
  key,
  partNumber,
  abortController,
}: {
  uploadId: string;
  key: string;
  partNumber: number;
  abortController?: AbortController;
}) {
  logger.info(`Getting signed URL for part ${partNumber}`);

  const response = await crudUtil.post(
    `${transcoder}${signinPartUpload}`,
    {
      key,
      uploadId,
      partNumber,
    },
    {
      signal: abortController?.signal,
    }
  );
  logger.info(`Signed URL for part ${partNumber} obtained successfully`);
  return {
    url: response.url,
    headers: response.headers,
  };
}

export async function abortUpload({
  key,
  uploadId,
  videoGuid,
  abortController,
}: {
  uploadId: string;
  key: string;
  videoGuid: string;
  abortController?: AbortController;
}) {
  const response = await crudUtil.post(
    `${transcoder}${abortUploadRoute}`,
    {
      uploadId,
      key,
      videoGuid,
    },
    {
      signal: abortController?.signal,
    }
  );
  return response.data;
}

// Upload the chunk using pre-signed URL with abort signal
export async function uploadChunk(
  chunkData: IChunkUploadData,
  signedUrl: { url: string; headers?: any },
  abortController?: AbortController
): Promise<{
  ETag: string;
  PartNumber: number;
  "content-length": string;
  etag: string;
}> {
  let uploadResponse;
  try {
    // Use fetch API for better worker compatibility
    const fetchResponse = await fetch(signedUrl.url, {
      method: 'PUT',
      body: chunkData.chunkData,
      headers: signedUrl.headers || {},
      signal: abortController?.signal,
    });
    
    if (!fetchResponse.ok) {
      const errorText = await fetchResponse.text();
      throw new Error(`Upload failed: ${fetchResponse.status} ${fetchResponse.statusText} - ${errorText}`);
    }
    
    // Extract ETag from response headers
    const etag = fetchResponse.headers.get('etag') || fetchResponse.headers.get('ETag');
    
    // Create a response object compatible with the rest of the code
    uploadResponse = {
      status: fetchResponse.status,
      headers: {
        etag: etag,
      },
    };
  } catch (error: any) {
    logger.error(`Upload failed for chunk ${chunkData.chunkNo}:`, error?.message);
    throw error;
  }

  // Extract ETag from response headers
  const etag = uploadResponse.headers.etag;
  
  if (!etag) {
    console.error('❌ [uploadChunk] NO ETAG in response headers:', {
      chunkNo: chunkData.chunkNo,
      headers: uploadResponse.headers,
      status: uploadResponse.status
    });
  }

  logger.info(`Chunk ${chunkData.chunkNo} uploaded successfully, ETag: ${etag}`);
  return {
    ETag: etag || `"missing-etag-${chunkData.chunkNo}"`,
    PartNumber: chunkData.chunkNo,
    "content-length": (chunkData.chunkData?.byteLength || 0).toString(),
    etag: etag || `"missing-etag-${chunkData.chunkNo}"`,
  };
}

// Complete multipart upload
export async function completeMultipartUpload({
  uploadId,
  key,
  parts,
  abortController,
}: {
  uploadId: string;
  key: string;
  parts: Array<{ ETag: string; PartNumber: number; "content-length": string }>;
  abortController?: AbortController;
}) {
  const response = await crudUtil.post(
    `${transcoder}${completeMultipartUploadRoute}`,
    {
      uploadId,
      key,
      parts,
    },
    {
      signal: abortController?.signal,
    }
  );
  return response;
}

// Get share link for video
export async function getVideoShareLink({
  videoId,
  abortController,
}: {
  videoId: string; // videoId without hyphens
  abortController?: AbortController;
}) {
  // Convert videoId without hyphens to proper UUID format for API

  const response = await crudUtil.post(
    videoShareLink,
    {
      CommentsAccessType: 1,
      ExpiryDate: null,
      FolderId: null,
      LinkPassword: "",
      VideoGuid: videoId,
    },
    {
      signal: abortController?.signal,
    }
  );
  return response;
}
